import { useState } from 'react'
import { Alert, Button, Group, Select, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconBellPlus, IconInfoCircle } from '@tabler/icons-react'
import { WidgetCard } from '@/components/ui'
import { subscribeToPush, pushSubscriptionsApi } from '@/api/pushSubscriptions'
import { alertSubscriptionApi } from '@/api/alerts'
import { isStandalonePWA } from '@/utils/pwa'

// Valores são strings Severity aceitas pelo backend SubscriptionUpsert.min_severity
const SEV_OPTIONS = [
  { value: 'warning', label: 'Warning ou acima' },
  { value: 'critical', label: 'High / Disaster (recomendado)' },
]

const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent)

export function AlertOptInCard() {
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [minSev, setMinSev] = useState('critical')
  const [testCooldown, setTestCooldown] = useState(false)

  async function enable() {
    setBusy(true)
    try {
      const sub = await subscribeToPush()
      if (!sub) {
        notifications.show({
          color: 'red',
          title: 'Push indisponível',
          message:
            'Permita notificações e use o Chrome no Android (adicione à tela inicial).',
        })
        return
      }
      await alertSubscriptionApi.enableZabbixWebpush(minSev)
      setDone(true)
      notifications.show({
        color: 'teal',
        title: 'Pronto',
        message: 'Você receberá alertas no celular.',
      })
    } catch {
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: 'Falha ao ativar push.',
      })
    } finally {
      setBusy(false)
    }
  }

  async function sendTest() {
    setTestCooldown(true)
    try {
      const result = await pushSubscriptionsApi.sendTest()
      if (result.sent > 0) {
        notifications.show({
          color: 'green',
          title: 'Enviado',
          message: 'Enviei — deve chegar em instantes.',
        })
      } else {
        notifications.show({
          color: 'yellow',
          title: 'Nenhum destino',
          message: 'Ative as notificações primeiro.',
        })
      }
    } catch {
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: 'Falha ao enviar notificação de teste.',
      })
    } finally {
      setTimeout(() => setTestCooldown(false), 3000)
    }
  }

  const pushGranted =
    typeof Notification !== 'undefined' && Notification.permission === 'granted'

  // iOS + não instalado: guia o usuário antes de exibir o fluxo normal
  if (isIOS && !isStandalonePWA()) {
    return (
      <WidgetCard accent="info">
        <Stack gap="xs">
          <Text fw={600}>Receber alertas no celular</Text>
          <Alert
            icon={<IconInfoCircle size={16} />}
            color="blue"
            variant="light"
          >
            No iPhone, instale o app primeiro: toque em{' '}
            <strong>Compartilhar</strong> → <strong>"Adicionar à Tela de Início"</strong>. Só
            assim o iPhone entrega notificações.
          </Alert>
        </Stack>
      </WidgetCard>
    )
  }

  return (
    <WidgetCard accent="info">
      <Stack gap="xs">
        <Text fw={600}>Receber alertas no celular</Text>
        <Text size="sm" c="dimmed">
          Instale o ConsoleAI na tela inicial (Chrome → "Adicionar à tela inicial") e ative as
          notificações para receber os alertas do Zabbix por push — sem WhatsApp.
        </Text>
        <Group align="end">
          <Select
            label="Severidade mínima do push"
            data={SEV_OPTIONS}
            value={minSev}
            onChange={(v) => setMinSev(v || '2')}
            w={260}
          />
          <Button
            leftSection={<IconBellPlus size={16} />}
            loading={busy}
            disabled={done}
            onClick={enable}
          >
            {done ? 'Push ativado' : 'Receber alertas neste aparelho'}
          </Button>
          {pushGranted && (
            <Button
              variant="light"
              color="blue"
              loading={testCooldown}
              disabled={testCooldown}
              onClick={sendTest}
            >
              Enviar notificação de teste
            </Button>
          )}
        </Group>
      </Stack>
    </WidgetCard>
  )
}
